import {
  List,
  TextInput,
  ReferenceInput,
  SelectInput,
  Datagrid,
  TextField,
  DateInput,
  FunctionField,
  ReferenceField,
  DateField,
  Button,
  SimpleList,
} from "react-admin";
import { EmployeeServicePath, SICK_LEAVES, STAFF_REQUEST, StaffRequestTypes, MOBILE_BREAKPOINT, StaffRequestStatuses } from "utils/constants";
import { StaffRequestStatus } from "utils/choices";
import { useNavigate } from "react-router";
import EmployeesField from "server/components/Fields/EmployeesField";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, useMediaQuery } from "@mui/material";
import { administrationRequests } from "api/administrationApi";
import { employeeRequests } from "api/employeeApi";
import SecuredComponent from "server/security/SecuredComponent";
import permissions from "app/permissions";
import { EmptyList } from "server/components";
import { settings } from "app";
import { MobileListRow } from "features/MobileListRow";
import CancelRequestDialog from "../components/CancelRequestDialog";

const filters = [
  <TextInput
    label="Име"
    source="name"
    alwaysOn
  />,
  <ReferenceInput
    source="structuralUnit"
    reference={administrationRequests.getStructuralUnitsSelect()}
    label="Факултет"
    alwaysOn
  >
    <SelectInput
      label="Факултет"
      emptyText="Всички"
      style={{ minWidth: 280 }}
    />
  </ReferenceInput>,
  <ReferenceInput
    source="department"
    reference={administrationRequests.getDepartmentsSelect()}
    label="Катедра"
    alwaysOn
  >
    <SelectInput
      label="Катедра"
      emptyText="Всички"
      alwaysOn
    />
  </ReferenceInput>,
  <DateInput
    label="От дата"
    source="startDate"
    alwaysOn
  />,
  <DateInput
    label="До дата"
    source="endDate"
    alwaysOn
  />,
];

const statusLabel = rec => StaffRequestStatus.find(s => s.id === rec?.staffRequest?.status)?.name ?? "-";

const RowActions = ({ rec, navigate, iconOnly = false }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      alignItems: "center",
      justifyItems: "center",
    }}
  >
    <Box>
      <Button
        label={iconOnly ? "" : "Детайли"}
        onClick={() =>
          navigate(`/${EmployeeServicePath}details/${STAFF_REQUEST}/${rec?.id}`, {
            state: { type: StaffRequestTypes.SickLeave, readOnly: true },
          })
        }
      >
        <VisibilityIcon />
      </Button>
    </Box>
    <Box>
      {rec?.staffRequest?.status === StaffRequestStatuses.Pending && (
        <SecuredComponent permission={permissions.canUpdateLeaveRequests}>
          <Button
            label={iconOnly ? "" : "Редактиране"}
            onClick={() => navigate(`/${EmployeeServicePath}edit/${SICK_LEAVES}/${rec?.id}`)}
          >
            <EditIcon />
          </Button>
        </SecuredComponent>
      )}
    </Box>
    <Box>
      <SecuredComponent permission={permissions.canUpdateLeaveRequests}>
        <CancelRequestDialog
          record={rec}
          requestType={StaffRequestTypes.SickLeave}
          iconOnly={iconOnly}
        />
      </SecuredComponent>
    </Box>
  </Box>
);

const MobileView = ({ navigate }) => (
  <SimpleList
    empty={<EmptyList />}
    linkType={false}
    sx={settings.style.mobileList}
    primaryText={rec => (
      <div>
        <MobileListRow fields={[{ label: "№", value: rec.id }]} />
        <MobileListRow fields={[{ label: "Именa", value: <EmployeesField source="employeeId" /> }]} />
      </div>
    )}
    secondaryText={rec => (
      <div>
        <MobileListRow fields={[{ label: "Начална дата", value: <DateField source="dateFrom" locales="bg-BG" /> }]} />
        <MobileListRow fields={[{ label: "Крайна дата", value: <DateField source="dateTo" locales="bg-BG" /> }]} />
        <MobileListRow fields={[{ label: "№ документ", value: rec?.documentId }]} />
        <MobileListRow
          fields={[
            {
              label: "Тип",
              value: (
                <ReferenceField
                  source="sickLeaveType"
                  reference={employeeRequests.getSickLeaveTypesSelect()}
                  link={false}
                >
                  <TextField source="nameBg" />
                </ReferenceField>
              ),
            },
          ]}
        />
        <MobileListRow fields={[{ label: "Статус", value: statusLabel(rec) }]} />
        <MobileListRow
          fields={[
            {
              label: "Дата на подаване",
              value: <DateField source="staffRequest.createdOn" locales="bg-BG" emptyText="—" />,
            },
          ]}
        />
      </div>
    )}
    tertiaryText={rec => (
      <RowActions
        rec={rec}
        navigate={navigate}
        iconOnly
      />
    )}
  />
);

const DesktopView = ({ navigate }) => (
  <Datagrid
    sx={{
      "& .RaDatagrid-headerCell": { textAlign: "left" },
      "& .RaDatagrid-headerCell:last-of-type, & .RaDatagrid-rowCell:last-of-type": { textAlign: "center" },
    }}
  >
    <TextField
      source="id"
      label="№"
      sortable={false}
    />
    <EmployeesField
      source="employeeId"
      label="Именa"
      sortable={false}
    />
    <DateField
      source="staffRequest.createdOn"
      label="Дата на подаване"
      locales="bg-BG"
      sortable={false}
      emptyText="—"
    />
    <DateField
      source="dateFrom"
      label="Начална дата"
      locales="bg-BG"
      sortable={false}
    />
    <DateField
      source="dateTo"
      label="Крайна дата"
      locales="bg-BG"
      sortable={false}
    />
    <TextField
      source="documentId"
      label="№ документ"
      sortable={false}
    />
    <ReferenceField
      source="sickLeaveType"
      reference={employeeRequests.getSickLeaveTypesSelect()}
      label="Тип"
      link={false}
      sortable={false}
    >
      <TextField source="nameBg" />
    </ReferenceField>
    <FunctionField
      label="Статус"
      sortable={false}
      render={statusLabel}
    />
    <FunctionField
      label="Действия"
      render={rec => (
        <RowActions
          rec={rec}
          navigate={navigate}
        />
      )}
    />
  </Datagrid>
);

const SickLeavesList = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  return (
    <List
      title="Болнични"
      filters={filters}
      exporter={false}
    >
      {isMobile ? <MobileView navigate={navigate} /> : <DesktopView navigate={navigate} />}
    </List>
  );
};

export default SickLeavesList;
